// scheduling/components/pool/MyPoolHistory.jsx
// "My recent pool work" — jobs the viewer claimed or was assigned (Trello #257).
//
// This is the closing half of the hand-off. RPRS stops tracking a job once it's
// claimed (per DJB, tracking lives in PE after that), so instead of a progress
// view this is a record with a way back into PE for each job.
//
// The work status shown is rprs.Jobs.StatusDetail, which peSync already keeps
// current — so it reflects PE's live state without RPRS asking PE anything, and
// without needing any completion loop.

import { POOL_META } from '../../utils/poolEligibility';
import { JOB_STATUS } from '../../utils/schedConstants';

const PE_JOB_URL = (peJobIndex) => `https://bmss.pehosted.com/PE/Job/Job/${peJobIndex}?panel=0`;

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return `today ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function MyPoolHistory({ jobs, loading }) {
  if (loading) return <div className="sched-pool-side-empty">Loading…</div>;
  if (!jobs || jobs.length === 0) {
    return <div className="sched-pool-side-empty">You haven&rsquo;t claimed any pool work yet.</div>;
  }

  return (
    <div className="sched-pool-history">
      {jobs.map(j => {
        const statusStyle = JOB_STATUS[j.StatusDetail] || null;
        return (
          <div key={j.PoolJobId} className="sched-pool-history-row">
            <div className="sched-pool-history-head">
              <span className="sched-pool-history-client">{j.ClientName}</span>
              <span className="sched-pool-chip sched-pool-chip--sm" style={{ background: POOL_META[j.PoolCode]?.color || '#6B7280' }}>
                {POOL_META[j.PoolCode]?.label || j.PoolCode}
              </span>
            </div>
            <div className="sched-pool-history-name">{j.JobName}</div>
            <div className="sched-pool-history-meta">
              <span>
                {j.ClaimKind === 'assigned'
                  ? `Assigned by ${j.AssignedByName || 'a manager'}`
                  : 'Claimed'} {fmtWhen(j.ClaimedAt)}
              </span>
              {j.StatusDetail && (
                <span
                  className="sched-pool-history-status"
                  style={statusStyle ? { background: statusStyle.bg, color: statusStyle.color } : undefined}
                >
                  {j.StatusDetail}
                </span>
              )}
            </div>
            {j.PEJobIndex && (
              <a
                className="sched-pool-history-pe"
                href={PE_JOB_URL(j.PEJobIndex)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in PE ↗
              </a>
            )}
            {/* A failed PE write means RPRS and PE disagree about this job — the
                staffer should know rather than discover it in PE later. */}
            {j.PeWriteState === 'failed' && (
              <div className="sched-pool-history-pefail">
                ⚠ This didn&rsquo;t finish writing to PE ({j.PeWriteStep || 'unknown step'}). Tell an admin.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
